// src/pages/Login/LoginPage.jsx

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Container, CssBaseline, Box, Typography, TextField, Button, Avatar, Paper, Switch, FormControlLabel } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "react-toastify/dist/ReactToastify.css";
// import socket from "../../socket/Socket.js";

const theme = createTheme({
	palette: {
		mode: "dark",
		primary: {
			main: "#01a79e",
		},
		background: {
			default: "#121212",
			paper: "#1d1d1d",
		},
		text: {
			primary: "#ffffff",
		},
	},
	typography: {
		fontFamily: "'Orbitron', sans-serif",
	},
});

const LoginPage = ({ setAuthenticated }) => {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [isEducator, setIsEducator] = useState(false);
	const usernameRef = useRef("");
	const passwordRef = useRef("");

	useEffect(() => {
		usernameRef.current = username;
		passwordRef.current = password;
	}, [username, password]);

	// Load user type preference on component mount
	useEffect(() => {
		const savedUserType = localStorage.getItem("userTypePreference");
		if (savedUserType === "educator") {
			setIsEducator(true);
		}
	}, []);

	// Save user type preference whenever it changes
	useEffect(() => {
		localStorage.setItem("userTypePreference", isEducator ? "educator" : "student");
	}, [isEducator]);

	const handleLogin = async (e) => {
		if (e && e.preventDefault) {
			e.preventDefault();
		}

		try {
			let response;

			if (isEducator) {
				// Educator login
				response = await axios.get("https://edu-backend-bafjgsfbapfxdecb.westus2-01.azurewebsites.net/api/v1/user/details", {
					params: {
						email: username || usernameRef.current,
						password: encodeURIComponent(password || passwordRef.current),
					},
					headers: {
						"x-api-key": "nqzq1qou481sd01k7v1qsl92u5k8axu3goz4el8j",
					},
				});

				if (response.data && response.status === 200 && response.data.success) {
					const data = response.data.data;

					const user = {
						...data,
						id: data._id,
						name: data.first_name + " " + data.last_name,
						role: "educator",
						password: password || passwordRef.current,
					};

					// Save user to the specified endpoint
					try {
						await axios.post("https://mitserver.app:9005/saveIqUserData", { data: user });
					} catch (saveError) {
						console.error("Error saving educator user:", saveError);
					}

					localStorage.setItem("user", JSON.stringify(user));
					localStorage.setItem("token", "educator-token");
					localStorage.setItem("userType", "educator");
					localStorage.setItem("hasForceRelogin", "true");
					setAuthenticated(true);
				} else {
					toast.dark("Invalid credentials", { pauseOnHover: false });
				}
			} else {
				const distid = username || usernameRef.current;
				const pwd = encodeURIComponent(password || passwordRef.current);
				response = await axios.get(
					`https://shield.iqonic.life/outerinfo.dhtml?webhook=ite5r9Qtin82q&action=verifylogin&distid=${distid}&password=${pwd}`
				);

				if (response.data && Array.isArray(response.data) && response.data.length > 0) {
					const data = response.data[0];
					console.log("LOGIN SUCCESS:", data);

					if (data.active !== "Active") {
						toast.dark("Account is inactive", { pauseOnHover: false });
						return;
					}

					if (data.expiration) {
						const expirationDate = new Date(data.expiration);
						const currentDate = new Date();
						currentDate.setHours(0, 0, 0, 0);
						expirationDate.setHours(0, 0, 0, 0);

						if (currentDate > expirationDate) {
							toast.error("Your account has expired", { pauseOnHover: false });
							return;
						}
					}

					// if (data["IQ Charts"] !== "Y") {
					// 	toast.dark("IQ Charts access is not enabled for this account", { pauseOnHover: false });
					// 	return;
					// }

					const token = data.uuid;

					const userWithPassword = {
						...data,
						role: "student",
						password: password || passwordRef.current,
					};

					// Save user to the specified endpoint
					try {
						await axios.post("https://mitserver.app:9005/saveIqUserData", { data: userWithPassword });
					} catch (saveError) {
						console.error("Error saving student user:", saveError);
					}

					localStorage.setItem("user", JSON.stringify(userWithPassword));
					localStorage.setItem("token", token);
					localStorage.setItem("userType", "student");
					localStorage.setItem("hasForceRelogin", "true");

					// const selectedAccount = {
					// 	account: "demo",
					// 	userId: data.userid,
					// 	token: token,
					// };

					// connectSocket(data, token);

					// localStorage.setItem("selectedAccount", JSON.stringify(selectedAccount));
					setAuthenticated(true);

					// localStorage.setItem("contractCategory", "multiplier");
				} else {
					console.log("LOGIN ERROR:", response);
					toast.dark("Invalid credentials", { pauseOnHover: false });
				}
			}
		} catch (error) {
			console.error("LOGIN ERROR:", error);
			toast.dark("Login failed. Please try again.", { pauseOnHover: false });
		}
	};

	const handleForgotPassword = async () => {
		try {
			if (username === "") {
				toast.dark("Please enter your email address", { pauseOnHover: false });
				return;
			}

			if (isEducator) {
				toast.dark("Password recovery for educators is not available. Please contact support.", { pauseOnHover: false });
				return;
			}

			const response = await axios.post("https://mitserver.app:9005/forgot", {
				email: username,
			});
			if (response.status === 200) {
				toast.dark(response.data.message || "Password sent", { pauseOnHover: false });
			} else {
				toast.dark(response.data.message || "Error sending email", { pauseOnHover: false });
			}
		} catch (error) {
			toast.dark(error.response?.data?.message || "An error occurred. Please try again.", { pauseOnHover: false });
		}
	};

	useEffect(() => {
		const handleLoginFromMessage = () => {
			handleLogin();
		};

		const handleIncomingMessage = (event) => {
			try {
				// Skip non-object messages or messages that don't look like JSON
				if (typeof event.data === "string" && !event.data.startsWith("{")) {
					return; // Ignore non-JSON string messages
				}

				const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

				if (data && data.email && data.password) {
					setUsername(data.email);
					setPassword(data.password);
					console.log("Data received:", data);
					setTimeout(handleLoginFromMessage, 100);
				}
			} catch (error) {
				// Silently ignore parsing errors from non-JSON messages
				console.debug("Ignored non-JSON message:", event.data);
			}
		};

		window.addEventListener("message", handleIncomingMessage);

		return () => {
			window.removeEventListener("message", handleIncomingMessage);
		};
	}, [handleLogin]);

	return (
		<ThemeProvider theme={theme}>
			<Box
				sx={{
					backgroundImage: 'url("/assets/loginBg.jpg")',
					backgroundSize: "cover",
					backgroundPosition: "center",
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexDirection: { xs: "column", md: "row" },
					padding: { xs: "20px", md: 0 },
				}}>
				<CssBaseline />
				<Container
					component="main"
					maxWidth="xs"
					sx={{
						marginLeft: { xs: "0", md: "40%" },
						width: { xs: "100%", md: "auto" },
					}}>
					<Paper
						elevation={6}
						style={{
							padding: "20px",
							borderRadius: "10px",
							background: "transparent",
							backgroundColor: "rgba(0, 0, 0, 0.6)",
						}}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
							}}>
							<Box sx={{ m: 1, width: { xs: "70%", md: "100%" }, display: "flex", justifyContent: "center" }}>
								<img src="/assets/IQA logo.png" alt="Login" style={{ width: "50%" }} />
							</Box>
							<Typography
								component="h1"
								variant="h5"
								sx={{
									color: "#fff",
									textShadow: "0 0 10px #4599d9",
									marginTop: { xs: "10px", md: "20px" },
									fontSize: { xs: "1.5rem", md: "h3.fontSize" },
								}}>
								IQ CHARTS
							</Typography>
							<Typography
								component="h1"
								variant="h5"
								sx={{
									color: "#fff",
									textShadow: "0 0 10px #4599d9",
									marginTop: { xs: "20px", md: "30px" },
									fontSize: { xs: "1.5rem", md: "h5.fontSize" },
								}}>
								Login
							</Typography>
							<Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: "100%" }}>
								<TextField
									margin="normal"
									required
									fullWidth
									id="username"
									label="E-mail"
									name="username"
									autoComplete="username"
									autoFocus
									value={username}
									onChange={(e) => setUsername(e.target.value)}
									InputLabelProps={{ style: { color: "#ffffff" } }}
									InputProps={{
										style: { color: "#ffffff" },
										sx: {
											"& fieldset": { borderColor: "#4599d9" },
											"&:hover fieldset": { borderColor: "#f74712" },
											"&.Mui-focused fieldset": { borderColor: "#f74712" },
										},
									}}
								/>
								<TextField
									margin="normal"
									required
									fullWidth
									name="password"
									label="Password"
									type="password"
									id="password"
									autoComplete="current-password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									InputLabelProps={{ style: { color: "#ffffff" } }}
									InputProps={{
										style: { color: "#ffffff" },
										sx: {
											"& fieldset": { borderColor: "#4599d9" },
											"&:hover fieldset": { borderColor: "#f74712" },
											"&.Mui-focused fieldset": { borderColor: "#f74712" },
										},
									}}
								/>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									sx={{
										mt: 3,
										mb: 2,
										backgroundColor: "#4599d9",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#3d87c3",
										},
										boxShadow: "0 0 5px #4599d9, 0 0 5px #4599d9",
									}}>
									Login
								</Button>
								{/* <Typography align="center">
									<Link to="#" onClick={handleForgotPassword} style={{ color: "#f74712", textShadow: "0 0 5px#cc2e3d" }}>
										Forgot your password?
									</Link>
								</Typography> */}

								<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
									<FormControlLabel
										control={
											<Switch
												checked={isEducator}
												onChange={(e) => setIsEducator(e.target.checked)}
												sx={{
													"& .MuiSwitch-switchBase.Mui-checked": {
														color: "#4599d9",
													},
													"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
														backgroundColor: "#4599d9",
													},
												}}
											/>
										}
										label={
											<Typography sx={{ color: "#fff", fontSize: "1rem", fontWeight: "500" }}>
												{isEducator ? "Educator" : "Student"}
											</Typography>
										}
									/>
								</Box>
								{/* <Button
									fullWidth
									variant="contained"
									sx={{
										mt: 2,
										mb: 2,
										backgroundColor: "#833ab4",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#833ab4",
										},
										boxShadow: "0 0 5px #833ab4, 0 0 5px #833ab4",
									}}
									onClick={() => (window.location.href = "/register")}>
									Registrarse
								</Button> */}
							</Box>
						</Box>
					</Paper>
				</Container>
				{/* <Box
					sx={{
						m: 1,
						width: { xs: "70%", md: "auto" },
						display: { xs: "none", md: "block" },
					}}>
					<img src="/assets/ticksy3.png" alt="Login" width="100%" />
				</Box> */}
			</Box>
		</ThemeProvider>
	);
};

export default LoginPage;
